import { db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  collectionGroup
} from 'firebase/firestore';
import logger from '../utils/logger';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';

/**
 * Family Document Service
 * Handles Family Documents (public) and Family Vault (restricted)
 */

// ============================================
// FAMILY DOCUMENTS (Public - Admin Managed)
// ============================================

/**
 * Upload file to Family Documents (admin-only)
 * @param {string} familyCode - Family code
 * @param {File} file - File to upload
 * @param {Object} metadata - File metadata (including optional storageType: 'photos' | 'documents')
 * @returns {Promise<string>} Document ID
 */
export const uploadToFamilyDocuments = async (familyCode, file, metadata) => {
  try {
    const { category, uploadedBy, uploadedByName, storageType } = metadata;

    // Upload file to Firebase Storage
    const storagePath = `families/${familyCode}/familyDocuments/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    // Create Firestore document
    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyDocuments'), {
      fileName: file.name,
      filePath: storagePath,
      downloadURL,
      fileSize: file.size,
      fileType: file.type,
      category: category || 'general',
      storageType: storageType || 'documents', // 'photos' or 'documents'
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isPublic: true // Always true for family documents
    });

    return docRef.id;
  } catch (error) {
    logger.error('Error uploading to family documents:', error);
    throw error;
  }
};

/**
 * Get all Family Documents for a family
 * @param {string} familyCode - Family code
 * @param {string} storageType - Optional filter: 'photos' or 'documents'
 * @returns {Promise<Array>} List of documents
 */
export const getFamilyDocuments = async (familyCode, storageType = null) => {
  try {
    let q;

    if (storageType) {
      // Filter by storageType
      q = query(
        collection(db, 'families', familyCode, 'familyDocuments'),
        where('storageType', '==', storageType),
        orderBy('uploadedAt', 'desc')
      );
    } else {
      // Get all documents (for backwards compatibility)
      q = query(
        collection(db, 'families', familyCode, 'familyDocuments'),
        orderBy('uploadedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const documents = [];

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return documents;
  } catch (error) {
    logger.error('Error fetching family documents:', error);
    throw error;
  }
};

/**
 * Delete file from Family Documents (admin-only)
 * @param {string} familyCode - Family code
 * @param {string} docId - Document ID
 * @returns {Promise<void>}
 */
export const deleteFamilyDocument = async (familyCode, docId) => {
  try {
    // Get document to find file path
    const docRef = doc(db, 'families', familyCode, 'familyDocuments', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const docData = docSnap.data();

      // Delete file from storage
      const storageRef = ref(storage, docData.filePath);
      await deleteObject(storageRef);

      // Delete Firestore document
      await deleteDoc(docRef);
    }
  } catch (error) {
    logger.error('Error deleting family document:', error);
    throw error;
  }
};

// ============================================
// FAMILY VAULT (Restricted - Admin Only)
// ============================================

/**
 * Upload file to Family Vault (admin-only)
 * @param {string} familyCode - Family code
 * @param {File} file - File to upload
 * @param {Object} metadata - File metadata (including optional storageType: 'photos' | 'documents')
 * @returns {Promise<string>} Document ID
 */
export const uploadToFamilyVault = async (familyCode, file, metadata) => {
  try {
    const { category, uploadedBy, uploadedByName, storageType } = metadata;

    // Upload file to Firebase Storage (separate vault path)
    const storagePath = `families/${familyCode}/familyVault/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    // Create Firestore document
    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyVault'), {
      fileName: file.name,
      filePath: storagePath,
      downloadURL,
      fileSize: file.size,
      fileType: file.type,
      category: category || 'general',
      storageType: storageType || 'documents', // 'photos' or 'documents'
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isRestricted: true, // Always true for vault
      accessGrantedTo: [], // Empty by default, admin grants access
      accessGrantedUserIds: [] // For security rules checking
    });

    return docRef.id;
  } catch (error) {
    logger.error('Error uploading to family vault:', error);
    throw error;
  }
};

/**
 * Get all Family Vault documents for admin
 * @param {string} familyCode - Family code
 * @param {string} storageType - Optional filter: 'photos' or 'documents'
 * @returns {Promise<Array>} List of vault documents
 */
export const getFamilyVaultDocuments = async (familyCode, storageType = null) => {
  try {
    let q;

    if (storageType) {
      // Filter by storageType
      q = query(
        collection(db, 'families', familyCode, 'familyVault'),
        where('storageType', '==', storageType),
        orderBy('uploadedAt', 'desc')
      );
    } else {
      // Get all vault documents (for backwards compatibility)
      q = query(
        collection(db, 'families', familyCode, 'familyVault'),
        orderBy('uploadedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const documents = [];

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return documents;
  } catch (error) {
    logger.error('Error fetching family vault documents:', error);
    throw error;
  }
};

/**
 * Get vault documents accessible to a specific user
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of accessible vault documents
 */
export const getAccessibleVaultDocuments = async (familyCode, userId) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'familyVault'),
      where('accessGrantedTo', 'array-contains', { userId }),
      orderBy('uploadedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const documents = [];

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return documents;
  } catch (error) {
    logger.error('Error fetching accessible vault documents:', error);
    throw error;
  }
};

/**
 * Grant access to vault document for specific user(s)
 * @param {string} familyCode - Family code
 * @param {string} docId - Document ID
 * @param {Array} userAccess - Array of {userId, userName, permissions}
 * @param {string} grantedBy - Admin user ID
 * @returns {Promise<void>}
 */
export const grantVaultAccess = async (familyCode, docId, userAccess, grantedBy) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);

    // Add access for each user
    for (const access of userAccess) {
      await updateDoc(docRef, {
        accessGrantedTo: arrayUnion({
          userId: access.userId,
          userName: access.userName,
          permissions: access.permissions || 'view', // 'view' or 'download'
          grantedBy,
          grantedAt: Timestamp.now()
        }),
        accessGrantedUserIds: arrayUnion(access.userId)
      });
    }
  } catch (error) {
    logger.error('Error granting vault access:', error);
    throw error;
  }
};

/**
 * Revoke access to vault document for specific user
 * @param {string} familyCode - Family code
 * @param {string} docId - Document ID
 * @param {string} userId - User ID to revoke
 * @returns {Promise<void>}
 */
export const revokeVaultAccess = async (familyCode, docId, userId) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const docData = docSnap.data();
      const accessEntry = docData.accessGrantedTo.find(a => a.userId === userId);

      if (accessEntry) {
        await updateDoc(docRef, {
          accessGrantedTo: arrayRemove(accessEntry),
          accessGrantedUserIds: arrayRemove(userId)
        });
      }
    }
  } catch (error) {
    logger.error('Error revoking vault access:', error);
    throw error;
  }
};

/**
 * Delete file from Family Vault (admin-only)
 * @param {string} familyCode - Family code
 * @param {string} docId - Document ID
 * @returns {Promise<void>}
 */
export const deleteFamilyVaultDocument = async (familyCode, docId) => {
  try {
    // Get document to find file path
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const docData = docSnap.data();

      // Delete file from storage
      const storageRef = ref(storage, docData.filePath);
      await deleteObject(storageRef);

      // Delete Firestore document
      await deleteDoc(docRef);
    }
  } catch (error) {
    logger.error('Error deleting vault document:', error);
    throw error;
  }
};

// ============================================
// FAMILY DOCUMENTS - FOLDER SUPPORT
// ============================================

/**
 * Create a folder in Family Documents
 */
export const createFamilyDocumentFolder = async (familyCode, folderName, folderPath, uploadedBy, uploadedByName, storageType = 'documents') => {
  try {
    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyDocuments'), {
      fileName: folderName,
      type: 'folder',
      folderPath: folderPath,
      storageType,
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isPublic: true
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating family document folder:', error);
    throw error;
  }
};

/**
 * Upload file to Family Documents with folder path support
 */
export const uploadToFamilyDocumentsWithPath = async (familyCode, file, metadata, folderPath = '/') => {
  try {
    const { category, uploadedBy, uploadedByName, storageType } = metadata;
    const storagePath = `families/${familyCode}/familyDocuments/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyDocuments'), {
      fileName: file.name,
      filePath: storagePath,
      downloadURL,
      fileSize: file.size,
      fileType: file.type,
      category: category || 'general',
      storageType: storageType || 'documents',
      folderPath: folderPath,
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isPublic: true
    });

    return docRef.id;
  } catch (error) {
    logger.error('Error uploading to family documents with path:', error);
    throw error;
  }
};

/**
 * Rename a family document or folder
 */
export const renameFamilyDocument = async (familyCode, docId, newName) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyDocuments', docId);
    await updateDoc(docRef, { fileName: newName, updatedAt: serverTimestamp() });
  } catch (error) {
    logger.error('Error renaming family document:', error);
    throw error;
  }
};

/**
 * Move a family document to a different folder
 */
export const moveFamilyDocument = async (familyCode, docId, newFolderPath) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyDocuments', docId);
    await updateDoc(docRef, { folderPath: newFolderPath, updatedAt: serverTimestamp() });
  } catch (error) {
    logger.error('Error moving family document:', error);
    throw error;
  }
};

/**
 * Copy a family document to personal documents
 */
export const copyFamilyDocumentToPersonal = async (familyCode, docId, userId) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyDocuments', docId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Document not found');
    const docData = docSnap.data();
    if (docData.type === 'folder') throw new Error('Cannot copy folders');

    // Copy file in storage
    let newDownloadURL = docData.downloadURL;
    const newStoragePath = `families/${familyCode}/members/${userId}/personalDocuments/${Date.now()}_${docData.fileName}`;
    if (docData.downloadURL) {
      try {
        const response = await fetch(docData.downloadURL);
        const blob = await response.blob();
        const newStorageRef = ref(storage, newStoragePath);
        await uploadBytes(newStorageRef, blob);
        newDownloadURL = await getDownloadURL(newStorageRef);
      } catch (err) {
        logger.error('Error copying file in storage:', err);
      }
    }

    // Create document in personal documents
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocsRef = collection(memberRef, 'personalDocuments');
    const newDocRef = await addDoc(personalDocsRef, {
      title: docData.fileName,
      fileName: docData.fileName,
      fileUrl: newDownloadURL,
      fileType: docData.fileType,
      fileSize: docData.fileSize,
      storagePath: newStoragePath,
      folderPath: '/',
      ownerId: userId,
      familyCode,
      visibility: 'private',
      copiedFrom: 'familyDocuments',
      copiedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId
    });

    return newDocRef.id;
  } catch (error) {
    logger.error('Error copying family document to personal:', error);
    throw error;
  }
};

// ============================================
// FAMILY VAULT - FOLDER SUPPORT
// ============================================

/**
 * Create a folder in Family Vault
 */
export const createFamilyVaultFolder = async (familyCode, folderName, folderPath, uploadedBy, uploadedByName, storageType = 'documents') => {
  try {
    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyVault'), {
      fileName: folderName,
      type: 'folder',
      folderPath: folderPath,
      storageType,
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isRestricted: true,
      accessGrantedTo: [],
      accessGrantedUserIds: []
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating family vault folder:', error);
    throw error;
  }
};

/**
 * Upload file to Family Vault with folder path support
 */
export const uploadToFamilyVaultWithPath = async (familyCode, file, metadata, folderPath = '/') => {
  try {
    const { category, uploadedBy, uploadedByName, storageType } = metadata;
    const storagePath = `families/${familyCode}/familyVault/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    const docRef = await addDoc(collection(db, 'families', familyCode, 'familyVault'), {
      fileName: file.name,
      filePath: storagePath,
      downloadURL,
      fileSize: file.size,
      fileType: file.type,
      category: category || 'general',
      storageType: storageType || 'documents',
      folderPath: folderPath,
      uploadedBy,
      uploadedByName,
      uploadedAt: serverTimestamp(),
      isRestricted: true,
      accessGrantedTo: [],
      accessGrantedUserIds: []
    });

    return docRef.id;
  } catch (error) {
    logger.error('Error uploading to family vault with path:', error);
    throw error;
  }
};

/**
 * Rename a family vault document or folder
 */
export const renameFamilyVaultDocument = async (familyCode, docId, newName) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);
    await updateDoc(docRef, { fileName: newName, updatedAt: serverTimestamp() });
  } catch (error) {
    logger.error('Error renaming family vault document:', error);
    throw error;
  }
};

/**
 * Move a family vault document to a different folder
 */
export const moveFamilyVaultDocument = async (familyCode, docId, newFolderPath) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);
    await updateDoc(docRef, { folderPath: newFolderPath, updatedAt: serverTimestamp() });
  } catch (error) {
    logger.error('Error moving family vault document:', error);
    throw error;
  }
};

/**
 * Copy a family vault document to personal documents
 */
export const copyFamilyVaultDocumentToPersonal = async (familyCode, docId, userId) => {
  try {
    const docRef = doc(db, 'families', familyCode, 'familyVault', docId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Document not found');
    const docData = docSnap.data();
    if (docData.type === 'folder') throw new Error('Cannot copy folders');

    let newDownloadURL = docData.downloadURL;
    const newStoragePath = `families/${familyCode}/members/${userId}/personalDocuments/${Date.now()}_${docData.fileName}`;
    if (docData.downloadURL) {
      try {
        const response = await fetch(docData.downloadURL);
        const blob = await response.blob();
        const newStorageRef = ref(storage, newStoragePath);
        await uploadBytes(newStorageRef, blob);
        newDownloadURL = await getDownloadURL(newStorageRef);
      } catch (err) {
        logger.error('Error copying file in storage:', err);
      }
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocsRef = collection(memberRef, 'personalDocuments');
    const newDocRef = await addDoc(personalDocsRef, {
      title: docData.fileName,
      fileName: docData.fileName,
      fileUrl: newDownloadURL,
      fileType: docData.fileType,
      fileSize: docData.fileSize,
      storagePath: newStoragePath,
      folderPath: '/',
      ownerId: userId,
      familyCode,
      visibility: 'private',
      copiedFrom: 'familyVault',
      copiedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId
    });

    return newDocRef.id;
  } catch (error) {
    logger.error('Error copying family vault document to personal:', error);
    throw error;
  }
};

// ============================================
// SHARED WITH ME (Unified View)
// ============================================

/**
 * Get all files shared with a user
 * Includes: Personal documents shared + Vault documents with granted access
 * @param {string} userId - User ID
 * @param {string} familyCode - Family code
 * @returns {Promise<Array>} List of shared documents
 */
export const getSharedWithMe = async (userId, familyCode) => {
  try {
    const sharedDocs = [];

    // Get vault documents with granted access
    const vaultDocs = await getAccessibleVaultDocuments(familyCode, userId);
    vaultDocs.forEach(doc => {
      sharedDocs.push({
        ...doc,
        source: 'Family Vault',
        sharedType: 'vault'
      });
    });

    // Personal documents shared directly with this user — use a collectionGroup
    // query so we can search across all members' personalDocuments subcollections
    // within this family without knowing every member's UID upfront.
    try {
      const personalSharedQuery = query(
        collectionGroup(db, 'personalDocuments'),
        where('familyCode', '==', familyCode),
        where('sharedWith', 'array-contains', userId)
      );
      const personalSharedSnap = await getDocs(personalSharedQuery);
      personalSharedSnap.forEach(docSnap => {
        const data = docSnap.data();
        sharedDocs.push({
          id: docSnap.id,
          ...data,
          source: 'Personal',
          sharedType: 'personal',
          // Normalise display fields to match vault doc shape
          name: data.name || data.fileName || data.title || 'Untitled',
          fileUrl: data.fileUrl || data.url || null,
          uploadedAt: data.uploadedAt || data.createdAt || null,
        });
      });
    } catch (personalErr) {
      // Degraded gracefully — vault docs still show if personal query fails
      // (likely missing composite index; deploy firestore.indexes.json to fix)
      logger.warn('getSharedWithMe: personal docs query failed:', personalErr.message);
    }

    // Sort by upload date (most recent first)
    sharedDocs.sort((a, b) => {
      const aTime = a.uploadedAt?.toMillis() || 0;
      const bTime = b.uploadedAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return sharedDocs;
  } catch (error) {
    logger.error('Error fetching shared documents:', error);
    throw error;
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if user is family admin
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export const isFamilyAdmin = async (familyCode, userId) => {
  try {
    const memberRef = doc(db, 'families', familyCode, 'members', userId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      const memberData = memberDoc.data();
      return memberData.groups?.includes('ADMIN') || memberData.isAdmin === true;
    }

    return false;
  } catch (error) {
    logger.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get file icon based on file type
 * @param {string} fileType - MIME type
 * @returns {string} Icon name
 */
export const getFileIcon = (fileType) => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.includes('pdf')) return 'pdf';
  if (fileType.includes('word') || fileType.includes('document')) return 'document';
  if (fileType.includes('sheet') || fileType.includes('excel')) return 'spreadsheet';
  if (fileType.includes('presentation') || fileType.includes('powerpoint')) return 'presentation';
  return 'file';
};
